/**
 * The account's signing key (docs/plans/2026-10-25-signing-key.md; designed
 * with Alex on 2026-09-24, built 2026-10-25 for the map history).
 *
 * An Ed25519 key pair computed from the account's master key:
 *   seed = HKDF-SHA256(master key, info "orbital:signing-key:v1")
 * Nothing is stored: every device holding the master key computes the same
 * key, and it never changes (a password change or a recovery keeps the
 * master key). Its public half is published once (POST /api/signing-key; the
 * server keeps the first one and refuses another).
 *
 * What it signs, for now: "session Y is me" in a map's history (the server
 * records which editing session each change came on, the map says, signed,
 * which account each session is: docs/architecture/map-history.md). The
 * server never checks a signature; members' apps do, with the key they first
 * saw for that account (pinned on this device, like identity pins).
 */
import nacl from "tweetnacl";
import { on, Events } from "../core/events.js";

const INFO = "orbital:signing-key:v1";
const encoder = new TextEncoder();

const base64Of = (bytes) => btoa(String.fromCharCode(...new Uint8Array(bytes)));
const bytesOf = (base64) => Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));

/**
 * The signing key pair of the account with this master key.
 * @param {Uint8Array} masterKey
 * @returns {Promise<{publicKey: Uint8Array, secretKey: Uint8Array}>}
 */
export async function signingKeyFrom(masterKey) {
  const base = await crypto.subtle.importKey("raw", masterKey, "HKDF", false, ["deriveBits"]);
  const seed = new Uint8Array(await crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(32), info: encoder.encode(INFO) },
    base,
    256,
  ));
  try {
    return nacl.sign.keyPair.fromSeed(seed);
  } finally {
    seed.fill(0);
  }
}

// This account's key, computed once per sign-in.
let own = null; // { userId, publicKey, secretKey, published: Promise|null }

function forget() {
  own?.secretKey.fill(0);
  own = null;
}
on(Events.AUTH_LOGOUT, forget);
on(Events.AUTH_LOGIN, forget);

/**
 * This account's signing key (and published, the first time), or null when
 * no account is signed in here.
 * @returns {Promise<{userId: string, publicKey: Uint8Array, secretKey: Uint8Array}|null>}
 */
export async function ownSigningKey() {
  const [{ getCurrentUser }, { getMasterKey }] = await Promise.all([
    import("../account.js"),
    import("../auth/handlers.js"),
  ]);
  const userId = getCurrentUser()?.user_id;
  const masterKey = getMasterKey();
  if (!userId || !(masterKey instanceof Uint8Array)) return null;
  if (own?.userId !== userId) {
    forget();
    own = { userId, ...(await signingKeyFrom(masterKey)), published: null };
  }
  own.published ||= publish(own.publicKey).catch((error) => {
    own && (own.published = null); // tried again next time
    console.warn("[SigningKey] Not published yet:", error?.message);
  });
  return own;
}

async function publish(publicKey) {
  const response = await fetch("/api/signing-key", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    body: JSON.stringify({ public_key: base64Of(publicKey) }),
  });
  if (response.status === 409) {
    // Another key is published for this account: never replaced here.
    console.error("[SigningKey] The server holds a different signing key for this account");
    return;
  }
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
}

// ==================== Sessions in a map's history ====================

/** What a session entry signs. The map is not named: a session number is only
 *  ever given for one map. */
const sessionMessage = (session, client, user) =>
  encoder.encode(`orbital session v1|${session}|${client}|${user}`);

/**
 * "Session `session` (Yjs client `client`) is `user`", signed.
 * @returns {string} base64 signature
 */
export function signSession(key, session, client) {
  return base64Of(nacl.sign.detached(sessionMessage(session, client, key.userId), key.secretKey));
}

/** Whether `signature` is `user`'s, with their signing public key (base64). */
export function sessionSignatureValid({ session, client, user, signature }, publicKey) {
  try {
    return nacl.sign.detached.verify(sessionMessage(session, client, user), bytesOf(signature), bytesOf(publicKey));
  } catch {
    return false;
  }
}

// ==================== Others' keys, pinned on this device ====================

const PINS = "orbital_signing_pins_v1:";

/**
 * The signing key to check `userId`'s signatures with: the first one this
 * device saw for them (kept for the signed-in account), so a server that
 * swapped an account's published key cannot make its own signatures pass.
 * A published key that differs from the pinned one is not used (null).
 * @param {string} account - the signed-in account
 * @param {string} userId
 * @param {string|null} published - base64, from the server
 * @returns {string|null}
 */
export function pinnedSigningKey(account, userId, published, storage = globalThis.localStorage) {
  let pins = {};
  try { pins = JSON.parse(storage?.getItem(PINS + account) || "{}"); } catch { pins = {}; }
  if (pins[userId]) return pins[userId] === published ? published : null;
  if (!published) return null;
  pins[userId] = published;
  try { storage?.setItem(PINS + account, JSON.stringify(pins)); } catch { /* kept for this page only */ }
  return published;
}
